import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Alert, Button, FileLoader, Typography, COLORS, TextField, Radio, RadioGroup, Checkbox,
  Modal, ModalHeader, ModalContent, ModalFooter, ModalButtons,
} from 'pvs-design-system';
import { Box, Stack, LinearProgress } from '@mui/material';
import TreeSelectField from 'components/virtual-library/TreeSelectField';
import { SelectedFileDisplay } from 'components/virtual-library/CurrentFileDisplay';
import RoleListTooltip from 'components/virtual-library/RoleListTooltip';
import RoleCheckboxList from 'components/virtual-library/RoleCheckboxList';
import { Notify } from 'components/Notifications/Notifications';
import { extractErrorMessage } from 'utils/errorUtils';
import { validateResourceForm, type FormErrors } from 'utils/formValidation';
import { useAdminFormTrees, useModalClose } from 'hooks';
import {
  useResourceCreateInitQuery,
  useGetRolesQuery,
  useCreateAdminResourceMutation,
} from 'api/useLibraryApi';
import { AdminResourceType } from 'types';
import type { ExcludedLocation } from 'types/LibraryApi';
import { SHIMMER_PURPLE_SX } from 'constants/styles';
import ExcludeLocationsField from 'components/virtual-library/ExcludeLocationsField';
import ModalRouteBackground from 'components/virtual-library/ModalRouteBackground';

const MAX_DESC = 4000;

const EXCLUDE_LOCATIONS_HINT =
  'Use this to identify any locations that should not have access to the resource regardless of if they otherwise have permission to it.';

// ── Page ──────────────────────────────────────────────────────────────
const CreateResourcePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const navState = location.state as { lineage?: string } | null;
  const navLineage = navState?.lineage ?? '/Home/';

  // ── Form state ───────────────────────────────────────────────────
  const [locationId, setLocationId] = useState<number | null>(null);
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [resourceType, setResourceType] = useState<AdminResourceType>('URL');
  const [url, setUrl] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [fileLoaderKey, setFileLoaderKey] = useState(() => Date.now());
  const [description, setDescription] = useState('');
  const [isCascading, setIsCascading] = useState(false);
  const [audience, setAudience] = useState<'Everyone' | 'Specific Roles'>('Everyone');
  const [selectedRoleIds, setSelectedRoleIds] = useState<Set<number>>(new Set());

  const [errors, setErrors] = useState<FormErrors>({});
  const [bannerVisible, setBannerVisible] = useState(false);
  const [excludedLocations, setExcludedLocations] = useState<ExcludedLocation[]>([]);
  // Tree state and lazy loaders
  const {
    locationNodes,
    setLocationNodes,
    handleLoadLocationChildren,
    categoryNodes,
    fetchCategoriesForLocation,
    handleLoadCategoryChildren,
  } = useAdminFormTrees();

  // ── API ──────────────────────────────────────────────────────────
  const {
    locations,
    defaultLocationId,
    defaultCategoryId,
    isLoading: loadingLocations,
    isError: initError,
    error: initErrorObj,
  } = useResourceCreateInitQuery(navLineage);

  // Build a user-friendly message when init fails (shown as inline alert)
  const initErrorMessage = initError
    ? extractErrorMessage(initErrorObj, 'Failed to load resource form. Please try again.')
    : null;

  // Sync init locations into lazily-loaded state
  React.useEffect(() => {
    if (locations.length > 0) setLocationNodes(locations);
  }, [locations]);

  // Apply defaults from category-create-init (pre-select location and category)
  const [defaultsApplied, setDefaultsApplied] = useState(false);
  useEffect(() => {
    if (!defaultsApplied && defaultLocationId !== null) {
      setLocationId(defaultLocationId);
      setCategoryId(defaultCategoryId);
      fetchCategoriesForLocation(defaultLocationId);
      setDefaultsApplied(true);
    }
  }, [defaultLocationId, defaultCategoryId, defaultsApplied, fetchCategoriesForLocation]);

  const { data: roles = [], isLoading: loadingRoles } = useGetRolesQuery(locationId ?? undefined);
  const [createResource, { isLoading: saving }] = useCreateAdminResourceMutation();

  // Derive the name of the selected location for the Share label
  const selectedLocationName =
    locationNodes.find((l) => l.id === locationId)?.name ?? 'the selected location';

  // ── Handlers ─────────────────────────────────────────────────────
  const handleLocationSelect = useCallback((id: number) => {
    setLocationId(id);
    setCategoryId(null);
    setSelectedRoleIds(new Set());
    setBannerVisible(false);
    setErrors((prev) => ({ ...prev, location: undefined, roles: undefined }));
    // Fetch parent categories for the selected location
    fetchCategoriesForLocation(id);
  }, [fetchCategoriesForLocation]);

  const handleCategorySelect = useCallback((id: number) => {
    setCategoryId(id);
    setBannerVisible(false);
    setErrors((prev) => ({ ...prev, category: undefined }));
  }, []);

  const handleRoleToggle = useCallback((id: number) => {
    setSelectedRoleIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setBannerVisible(false);
    setErrors((prev) => ({ ...prev, roles: undefined }));
  }, []);

  const handleSubmit = async () => {
    const errs = validateResourceForm(
      locationId, categoryId, name, resourceType, url, file, audience, selectedRoleIds,
    );
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      setBannerVisible(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setErrors({});
    setBannerVisible(false);

    try {
      await createResource({
        locationId: locationId!,
        categoryId: categoryId!,
        name: name.trim(),
        resourceType,
        url: resourceType === 'URL' ? url.trim() : '',
        file: resourceType === 'File' ? (file ?? undefined) : undefined,
        description: description.trim(),
        isCascading,
        // If "Specific Roles" is selected but no roles are checked, fall back to
        // Everyone (isPublic: true, empty roleIds) so the resource is not inaccessible.
        isPublic: audience === 'Everyone' || selectedRoleIds.size === 0,
        roleIds: audience === 'Specific Roles' && selectedRoleIds.size > 0
          ? Array.from(selectedRoleIds)
          : [],
        excludedLocations,
      }).unwrap();

      const trimmedName = name.trim();
      Notify.success(`Resource '${trimmedName}' created successfully.`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      navigate(-1);
    } catch (e) {
      Notify.error(extractErrorMessage(e, 'Failed to create resource. Please try again.'));
    }
  };

  const hasErrors = Object.values(errors).some(Boolean);
  // useMemo prevents validateResourceForm (which parses URLs) from running on
  // every render — it only re-runs when a relevant form field actually changes.
  const isFormComplete = useMemo(
    () => Object.keys(
      validateResourceForm(locationId, categoryId, name, resourceType, url, file, audience, selectedRoleIds),
    ).length === 0,
    [locationId, categoryId, name, resourceType, url, file, audience, selectedRoleIds],
  );

  const handleClose = useModalClose();

  if (loadingLocations) {
    const shimmerSx = SHIMMER_PURPLE_SX;
    return (
      <>
      <ModalRouteBackground />
      <Modal open onClose={handleClose} closeModalOnBackdropClick ariaLabelledby="add-resource-title" PaperProps={{ sx: { width: '800px', maxWidth: '800px' } }}>
        <ModalHeader id="add-resource-title" title="Add resource" onClose={handleClose} />
        <ModalContent>
          <Stack gap={2.5}>
            {['Location', 'Folder', 'Name', 'Resource type', 'URL', 'Description', 'Sharing Settings', 'Audience'].map((_, i) => (
              <Box key={i}>
                <Box sx={{ ...shimmerSx, width: 60 + (i * 17) % 80, height: 14, mb: 1 }} />
                <Box sx={{ ...shimmerSx, width: '100%', height: i === 5 ? 100 : 40 }} />
              </Box>
            ))}
            <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
              <Box sx={{ ...shimmerSx, width: 90, height: 40, borderRadius: 1 }} />
              <Box sx={{ ...shimmerSx, width: 140, height: 40, borderRadius: 1 }} />
            </Box>
          </Stack>
        </ModalContent>
      </Modal>
      </>
    );
  }

  return (
    <>
    <ModalRouteBackground />
    <Modal open onClose={handleClose} closeModalOnBackdropClick ariaLabelledby="add-resource-title" PaperProps={{ sx: { width: '800px', maxWidth: '800px' } }}>
      <ModalHeader id="add-resource-title" title="Add resource" onClose={handleClose} />

      {/* Upload / save progress — shown while the mutation is in-flight.
          LinearProgress sits outside ModalContent so it stays visible even
          when the user has scrolled down inside the form. */}
      {saving && (
        <LinearProgress
          aria-label={resourceType === 'File' ? 'Uploading file…' : 'Saving resource…'}
          sx={{
            height: 4,
            borderRadius: 0,
            bgcolor: COLORS.gray[100],
            '& .MuiLinearProgress-bar': {
              background: COLORS.purple[700],
              borderRadius: 0,
            },
          }}
        />
      )}

      <ModalContent>
      {/* Inline init error — form stays visible but Save is disabled */}
      {initErrorMessage && (
        <Alert severity="error" sx={{ mb: 3 }}>{initErrorMessage}</Alert>
      )}

      {/* Upload / save progress message */}
      {saving && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {resourceType === 'File'
            ? 'Uploading your file—this may take a moment for large files. Please do not close this window.'
            : 'Saving resource…'}
        </Alert>
      )}

      {/* Feedback banners */}
      {bannerVisible && hasErrors && (
        <Alert severity="error" sx={{ mb: 3 }}>Please fix the highlighted errors before saving.</Alert>
      )}

      {/* ── Form ──────────────────────────────────────────── */}
      <form
        id="add-resource-form"
        noValidate
        onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}
        style={{ display: 'flex', flexDirection: 'column', gap: 20 }}
      >
        {/* 1. Name */}
        <Box style={{ maxWidth: 480 }}>
          <TextField
            id="res-name"
            label="Name"
            required
            value={name}
            size="medium"
            fullWidth
            inputProps={{ maxLength: 255 }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setName(e.target.value);
              setBannerVisible(false);
              if (e.target.value.trim()) setErrors((p) => ({ ...p, name: undefined }));
            }}
            error={!!errors.name}
            errorMessage={errors.name}
          />
        </Box>

        {/* 2. Location */}
        <Box style={{ maxWidth: 480 }}>
          <TreeSelectField
            id="res-location"
            label="Location"
            required
            nodes={locationNodes}
            selectedId={locationId}
            onSelect={handleLocationSelect}
            onLoadChildren={handleLoadLocationChildren}
            placeholder="Select a location…"
            loading={loadingLocations}
            error={!!errors.location}
            errorMessage={errors.location}
          />
        </Box>

        {/* 3. Category */}
        <Box style={{ maxWidth: 480 }}>
          <TreeSelectField
            id="res-category"
            label="Folder"
            required
            nodes={categoryNodes}
            selectedId={categoryId}
            onSelect={handleCategorySelect}
            onLoadChildren={handleLoadCategoryChildren}
            placeholder="Choose a Library Resource Folder"
            disabled={locationId === null}
            disabledPlaceholder="Select a location first"
            error={!!errors.category}
            errorMessage={errors.category}
          />
        </Box>

        {/* 4. Resource Type */}
        <RadioGroup
          id="res-resource-type"
          name="resourceType"
          row
          label="Resource type"
          value={resourceType}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setResourceType(e.target.value as AdminResourceType)}
        >
          {(['URL', 'File'] as AdminResourceType[]).map((t) => (
            <Radio
              key={t}
              id={`res-type-${t}`}
              label={t}
              name="resourceType"
              checked={resourceType === t}
              value={t}
            />
          ))}
        </RadioGroup>

        {/* 5. URL (conditional) */}
        {resourceType === 'URL' && (
          <Box style={{ maxWidth: 480 }}>
            <TextField
              id="res-url"
              label="URL"
              required
              type="url"
              value={url}
              size="medium"
              placeholder="https://"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setUrl(e.target.value);
                setBannerVisible(false);
                if (e.target.value.trim()) setErrors((p) => ({ ...p, url: undefined }));
              }}
              error={!!errors.url}
              errorMessage={errors.url}
              fullWidth
            />
          </Box>
        )}

        {/* 6. File (conditional) */}
        {resourceType === 'File' && (
          <Box style={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 480 }}>
            <Typography variant="p-md-semiBold" component="label" style={{ color: COLORS.gray[900], display: 'block' }}>
              File<span style={{ color: COLORS.red[800] }}> *</span>
            </Typography>
            {file ? (
              <SelectedFileDisplay
                file={file}
                onRemove={() => {
                  setFile(null);
                  setFileLoaderKey((k) => k + 1);
                }}
              />
            ) : (
              <FileLoader
                key={fileLoaderKey}
                loaderName={`res-file-create-${fileLoaderKey}`}
                fullWidth
                fileProps={{
                  sizeLimit: 100000000,
                  onFileLoadSuccess: (states) => {
                    setFile(states[0]?.file ?? null);
                    setBannerVisible(false);
                    setErrors((prev) => ({ ...prev, file: undefined }));
                  },
                  onFileLoadCancelled: () => setFile(null),
                }}
                formHelperTextProps={{ helperText: '100 MB maximum.' }}
              />
            )}
            {errors.file && (
              <span style={{ fontSize: 12, color: COLORS.red[800], fontWeight: 600 }} role="alert">{errors.file}</span>
            )}
          </Box>
        )}

        {/* 7. Description */}
        <Box style={{ maxWidth: 640 }}>
          <TextField
            id="res-description"
            label="Description"
            value={description}
            size="medium"
            fullWidth
            multiline
            rows={6}
            inputProps={{ maxLength: MAX_DESC }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}
            helperText={`${description.length}/${MAX_DESC}`}
          />
        </Box>

        {/* 8. Sharing Settings */}
        <Box style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Typography variant="p-md-semiBold" component="label" style={{ color: COLORS.gray[900], display: 'block' }}>
            Sharing Settings
          </Typography>
          <Checkbox
            id="res-cascading"
            checked={isCascading}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIsCascading(e.target.checked)}
            label={(<span>Checking this box will allow <strong>{selectedLocationName}</strong> and locations beneath it to see this document.</span>) as unknown as string}
          />
        </Box>

        {/* 8b. Exclude from locations — shown when Sharing Settings is enabled (mirrors Create Folder) */}
        {isCascading && locationId !== null && (
          <ExcludeLocationsField
            value={excludedLocations}
            locationId={locationId}
            onChange={setExcludedLocations}
            isCreateMode
            helperText={EXCLUDE_LOCATIONS_HINT}
          />
        )}

        {/* 9. Audience */}
        <Box style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <RadioGroup
            id="res-audience"
            name="audience"
            label="Audience"
            value={audience}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAudience(e.target.value as 'Everyone' | 'Specific Roles')}
          >
            {(['Everyone', 'Specific Roles'] as const).map((opt) => (
              <span key={opt} style={{ display: 'inline-flex', alignItems: 'center' }}>
                <Radio
                  id={`res-audience-${opt}`}
                  label={opt}
                  name="audience"
                  checked={audience === opt}
                  value={opt}
                />
                {opt === 'Specific Roles' && <RoleListTooltip />}
              </span>
            ))}
          </RadioGroup>

          {/* 10. Specific Roles (conditional) */}
          {audience === 'Specific Roles' && (
            <Box style={{ marginTop: 8, padding: '12px 16px', background: COLORS.gray[100], borderRadius: 6, border: `1px solid ${COLORS.gray[200]}` }} aria-label="Specific roles">
              {loadingRoles ? (
                <>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px 16px' }}>
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <Box key={i} sx={{ width: 80 + (i * 17) % 60, height: 20, borderRadius: 1, background: 'linear-gradient(90deg, #e0e0e0 25%, #f5f5f5 50%, #e0e0e0 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' }} />
                    ))}
                  </Box>
                </>
              ) : (
                <RoleCheckboxList
                  roles={roles}
                  selectedRoleIds={selectedRoleIds}
                  onToggle={handleRoleToggle}
                />
              )}
              {errors.roles && (
                <span style={{ fontSize: 12, color: '#d32f2f', fontWeight: 600 }} role="alert">{errors.roles}</span>
              )}
            </Box>
          )}
        </Box>

      </form>
      </ModalContent>

      <ModalFooter>
        <ModalButtons>
          <Button
            type="button"
            size="small"
            variant="text"
            color="secondary"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size="small"
            form="add-resource-form"
            variant="contained"
            color="primary"
            disabled={saving || !!initErrorMessage || !isFormComplete}
          >
            Save and Finish
          </Button>
        </ModalButtons>
      </ModalFooter>
    </Modal>
    </>
  );
};

export default CreateResourcePage;
